import type { Maybe, ValueEvent } from '@adornis/base/utilTypes.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { WindowSizeController } from '@adornis/chemistry/controllers/window-size-controller.js';
import { css } from '@adornis/chemistry/directives/css.js';
import type { FormController } from '@adornis/forms/x-form-controller.js';
import type { TranslationController } from '@adornis/translation-core/client/translation-controller.js';
import { FormOfFinancing } from '@chancenwerk/_cms/financing/entities/FormOfFinancing.js';
import { FormOfFinancingBuT } from '@chancenwerk/_cms/financing/entities/FormOfFinancingBuT.js';
import { FormOfFinancingMitgliedsbeitrag } from '@chancenwerk/_cms/financing/entities/FormOfFinancingMitgliedsbeitrag.js';
import type { ChromiumToggleButton } from '@chancenwerk/design/components/cr-toggle-button.js';
import { FormOfFinancingLogicExpression } from '@chancenwerk/master-data-management/kpf/entities/FormOfFinancingLogicExpression.js';
import type { Student } from '@chancenwerk/master-data-management/users/entities/Student.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { createRef, ref, type Ref } from 'lit/directives/ref.js';
import { combineLatest, first, takeUntil } from 'rxjs';
import { KPF } from '../../../../src/master-data-management/kpf/entities/KPF.js';
import { AnmeldungPageRegistrationBase } from './anmeldung-component-formular-base.js';

const BUT_BERECHTIGUNG_DURCH = [
  'Bürgergeld (SGB II)',
  'Sozialhilfe (SGB XII) -> Hilfe zum Lebensunterhalt oder Grundsicherung im Alter und bei Erwerbsminderung',
  'Kinderzuschlag (BKGG § 6a)',
  'Wohngeld (WoGG)',
  'Asylbewerberleistungen (AsylbLG)',
  'Nicht sicher/keine Angabe',
];

/**
 * @customElement anmeldung-component-formular-but
 */
@customElement('anmeldung-component-formular-but')
export class AnmeldungPageRegistrationBut extends AnmeldungPageRegistrationBase {
  @state() private readonly _size = new WindowSizeController(this);

  @property({ attribute: false }) form!: FormController<Student>;
  @property({ attribute: false }) translation!: TranslationController<any>;
  @property({ attribute: false }) student!: RXController<Student>;
  @property({ attribute: false }) choosenKPF = new RXController<Maybe<KPF>>(this, null);

  @state() private _showBuT = false;
  @state() private _showBank = false;

  @state() private _errorOnLogicparsing = false;

  private readonly _lowerThan20Ref: Ref<ChromiumToggleButton> = createRef();

  override connectedCallback(): void {
    super.connectedCallback();

    // Set the kpf standart membership fee
    combineLatest([this.student.observable, this.choosenKPF.observable])
      .pipe(first(), takeUntil(this.disconnected))
      .subscribe(([student, kpf]) => (student.bankInformation.mitgliedsbeitrag = kpf?.standardMembershipFee));

    // TODO this is a first draft to show the required fields for but and bankinformation. Later the logic fomular should automatically build the form here
    this.choosenKPF.observable.pipe(takeUntil(this.disconnected)).subscribe(kpf => {
      const activatedFields = this._getAllVariablesFromLogicString(kpf?.formOfFinanzingLogicString);
      this._activateFieldsBasedOnLogicString(activatedFields);
    });
  }

  private _getAllVariablesFromLogicString(logicString: Maybe<FormOfFinancingLogicExpression | FormOfFinancing>) {
    if (!logicString) return null;

    if (logicString instanceof FormOfFinancing) return [logicString._class];

    if (logicString instanceof FormOfFinancingLogicExpression) {
      let result: any[] = [];
      for (let child of logicString.formsOfFinancingArray ?? []) {
        const childResult = this._getAllVariablesFromLogicString(child);
        if (childResult !== null) result = result.concat(childResult);
      }
      return result;
    }

    return null;
  }

  private _activateFieldsBasedOnLogicString(parsedLogic: any[] | null) {
    if (!parsedLogic) {
      this._errorOnLogicparsing = true;
      return;
    }

    if (parsedLogic?.includes(FormOfFinancingBuT._class)) {
      this._showBuT = true;
    }

    if (parsedLogic?.includes(FormOfFinancingMitgliedsbeitrag._class)) {
      this._showBank = true;
    }
  }

  override render() {
    if (this._errorOnLogicparsing)
      return html`
        <cr-flex>
          <cr-text bold ${css({ color: this.colors.tone.error })}>
            Beim Übersetzen der Finanzierungslogik ist ein Fehler aufgetreten! Wenden Sie sich bitte an die unten
            stehende E-Mail-Adresse und geben Sie den Namen der ausgewählten Schule mit an.
          </cr-text>
        </cr-flex>
      `;
    return html` <cr-flex space="xl">
      <cr-text>
        Für jedes Kind entstehen uns pro Monat Kosten von 60 €. Mithilfe unserer Unterstützer:innen reduziert sich der
        Betrag auf 20 €. Wenn auch Sie uns unterstützen möchten, dürfen Sie gerne mehr als 20 € zahlen
        (Wahl-Mitgliedsbeitrag). Ihnen ist es nicht möglich, den reduzierten Mitgliedsbeitrag von 20 € zu bezahlen?
        Bitte zeigen Sie uns dies im entsprechenden Feld im unteren Bereich an. Wir werden dies berücksichtigen und
        sicher eine Lösung finden! Sie verfügen über eine Berechtigung für das Bildungs- und Teilhabepaket (BuT)? Dann
        ändern Sie das nachfolgende Feld in „Ja“ und füllen die weiteren Felder aus, damit wir Ihnen die notwendigen
        Antragsunterlagen zuschicken können. Ab Bewilligung entfällt der Mitgliedsbeitrag komplett und die Teilnahme ist
        kostenlos!
      </cr-text>
      ${this._showBuT
        ? html`
            <cr-flex space="sm">
              <cr-h4 align="center" ${css({ padding: `0 ${this.spacing.sm} ${this.spacing.sm} ${this.spacing.sm}` })}>
                ${this.translation.translate('BuT_berechtigung')}
              </cr-h4>
              <cr-flex class="section-container without-image" space="xl">
                <cr-flex class="sc-content" crossaxis-start>
                  <cr-flex w100>
                    <cr-dropdown-selection
                      placeholder=${this.translation.translate('BuT_question')}
                      .value=${this.student.value.buts[0]?.BuTBerechtigung}
                      .selectables=${[true, false]}
                      .renderString=${(x: boolean) =>
                        x ? this.translation.translate('yes') : this.translation.translate('no')}
                      @value-picked=${(e: ValueEvent<boolean>) => {
                        const currentBut = this.student.value.buts[0];
                        if (currentBut?.BuTBerechtigung !== e.detail.value) {
                          if (currentBut) {
                            currentBut.BuTBerechtigung = e.detail.value;
                          }
                        }
                        this.requestUpdate();
                      }}
                      ${css({ width: '100%', maxWidth: this.sizes.maxReadableWidth })}
                    >
                    </cr-dropdown-selection>
                  </cr-flex>
                  ${this.student.value.buts[0]?.BuTBerechtigung ? this._getBUTIsTrueSection() : nothing}
                </cr-flex>
              </cr-flex>
            </cr-flex>
          `
        : nothing}
      ${(this.student.value.schoolInformation.jahrgangsstufe ?? 0) < 10 && this._showBank
        ? this._getMembershipSection()
        : nothing}

      <cr-flex horizontal end crossaxis-center space="sm">
        <cr-button
          @click=${async () => {
            this.form.validate().catch(console.error);
          }}
        >
          ${this.translation.translate('next')}
        </cr-button>
      </cr-flex>
    </cr-flex>`;
  }

  private _getMembershipSection() {
    return html`
      <cr-flex space="lg">
        <cr-flex space="sm">
          <cr-h3 align="center" ${css({ padding: `0 ${this.spacing.sm} ${this.spacing.sm} ${this.spacing.sm}` })}>
            Mitgliedsbeitrag
          </cr-h3>
          <cr-text>
            Der Mitgliedsbeitrag wird zum 1. eines jeden Monats per Lastschrift eingezogen. Bitte geben Sie dazu Ihre
            Kontodaten an. Wenn der Beitrag nicht eingezogen werden kann (z. B. durch fehlerhafte oder veraltete Daten,
            Rückbuchung etc.), erfolgt eine schriftliche Mahnung. Die Bearbeitungsgebühr der Bank müssen wir Ihnen in
            diesem Fall in Rechnung stellen. Chancenwerk e.V. behält sich vor, Ihr Kind nach erfolglosem Ablauf der
            Zahlungsfrist als Teilnehmer:in bei Angeboten von Chancenwerk e.V. zu streichen. Der Mitgliedsbeitrag ist
            auch in den Ferien zu bezahlen.
          </cr-text>
        </cr-flex>

        <cr-flex space="sm">
          <cr-h3 align="center" ${css({ padding: `0 ${this.spacing.sm} ${this.spacing.sm} ${this.spacing.sm}` })}>
            Kündigung
          </cr-h3>
          <cr-text>
            Sie können die Mitgliedschaft jederzeit mit einer Frist von 14 Tagen zum Monatsende kündigen. Die Kündigung
            muss schriftlich erfolgen, z. B. per Brief oder E-Mail. Die Adresse für Widerruf oder Kündigung finden Sie
            auf dem Chancenwerk-Infoblatt. Chancenwerk e.V. behält sich vor, die Mitgliedschaft zu kündigen, falls das
            Lernförderangebot nicht mehr angeboten werden kann.
          </cr-text>
        </cr-flex>

        <cr-flex space="sm">
          <cr-h3 align="center" ${css({ padding: `0 ${this.spacing.sm} ${this.spacing.sm} ${this.spacing.sm}` })}>
            Gläubiger-ID & Mandatsreferenz
          </cr-h3>
          <cr-text>
            Unsere Gläubiger-ID lautet: DE84BTR00001251018. Ihre Mandatsreferenz teilen wir Ihnen gesondert mit.
          </cr-text>
        </cr-flex>

        <cr-flex space="sm">
          <cr-h3 align="center" ${css({ padding: `0 ${this.spacing.sm} ${this.spacing.sm} ${this.spacing.sm}` })}>
            SEPA-Lastschriftmandat
          </cr-h3>
          <cr-text>
            Hiermit ermächtige ich Chancenwerk e.V. (Am Markt 18, 44575 Castrop-Rauxel) widerruflich, die von mir zu
            leistenden Zahlungen zum 1. eines jeden Monats von nachstehendem Konto durch Lastschrift einzuziehen.
            Zugleich weise ich meinen Zahlungsdienstleister an, die von Ihnen auf mein Konto gezogenen Lastschriften
            einzulösen.
          </cr-text>
          <cr-text>
            Hinweis: Ich kann innerhalb von acht Wochen, beginnend mit dem Belastungsdatum, die Erstattung des
            belasteten Betrages verlangen. Es gelten dabei die mit meinem Zahlungsdienstleister vereinbarten
            Bedingungen.
          </cr-text>
        </cr-flex>
        ${this._getBankInformation()}
      </cr-flex>
    `;
  }

  private _getBankInformation() {
    return html`
      <cr-flex class="section-container without-image">
        <cr-flex class="sc-content">
          <cr-flex space="md">
            <cr-flex horizontal space="sm">
              <cr-flex space="md" flex>
                ${!this._lowerThan20Ref.value?.value.value
                  ? html`
                      <cr-flex
                        horizontal
                        crossaxis-center
                        space=${this._size.mobile ? 'sm' : 'xxs'}
                        class="mobile--flex"
                        space-between
                      >
                        <cr-flex horizontal crossaxis-center space="sm">
                          <cr-text> ${this.translation.translate('i_want')} </cr-text>

                          <cr-dropdown-selection
                            placeholder-mode="inline"
                            ${this.form.field('bankInformation', 'mitgliedsbeitrag')}
                            .selectables=${[20, 25, 30, 40, 50, 60]}
                            .renderString=${(s: number) => `${s}€`}
                          >
                          </cr-dropdown-selection>
                        </cr-flex>
                        <cr-icon ${css({ cursor: 'pointer', color: this.colors.secondary, fontSize: '1.2rem' })}>
                          info
                          <cr-tooltip clickable>
                            <cr-text ${css({ maxWidth: '400px', padding: `0 ${this.spacing.sm}` })}>
                              ${this.translation.translate('tooltips', 'billing_contribution')}
                            </cr-text>
                          </cr-tooltip>
                        </cr-icon>
                      </cr-flex>
                    `
                  : nothing}
                <cr-flex horizontal space crossaxis-center space-between>
                  <cr-toggle-button
                    ${ref(this._lowerThan20Ref)}
                    ${this.form.field('bankInformation', 'membershipFeeLowerThan20')}
                    .label=${this.translation.translate('less_than_20')}
                  ></cr-toggle-button>
                  <cr-icon ${css({ cursor: 'pointer', color: this.colors.secondary, fontSize: '1.2rem' })}>
                    info
                    <cr-tooltip clickable>
                      <cr-text ${css({ maxWidth: '400px', padding: `0 ${this.spacing.sm}` })}>
                        Wenn es Ihnen nicht möglich ist, den reduzierten Mitgliedsbeitrag von 20 € zu bezahlen, dann
                        klicken Sie hier auf den Button. Wir werden dazu auf Sie zukommen und sicher eine Lösung finden.
                      </cr-text>
                    </cr-tooltip>
                  </cr-icon>
                </cr-flex>
              </cr-flex>
            </cr-flex>
            <cr-input
              ${this.form.field('bankInformation', 'kontoinhaber')}
              placeholder=${this.translation.translate('accountHolder')}
            ></cr-input>
            <cr-flex horizontal crossaxis-end space="md" class="mobile--flex">
              <cr-input
                ${this.form.field('bankInformation', 'iban')}
                placeholder="IBAN"
                type="iban"
                class="mobile--fullwidth"
                ${css({ width: '69%' })}
              ></cr-input>
              <cr-input
                ${this.form.field('bankInformation', 'bic')}
                placeholder="BIC"
                class="mobile--fullwidth"
                ${css({ width: '30%' })}
              ></cr-input>
            </cr-flex>
            <cr-input
              ${this.form.field('bankInformation', 'kreditinstitut')}
              placeholder=${this.translation.translate('creditInstitution')}
            ></cr-input>
          </cr-flex>
        </cr-flex>
      </cr-flex>
    `;
  }

  private _getBUTIsTrueSection() {
    return html`
      <cr-flex space="md">
        <cr-h4 ${css({ padding: `0 ${this.spacing.md}` })}>
          <b> ${this.translation.translate('pleaseFillout')} </b>
        </cr-h4>
        <cr-input
          ${this.form.field('buts', 0, 'antragsteller')}
          placeholder=${this.translation.translate('applicantName')}
        ></cr-input>
        <cr-dropdown-selection
          .selectables=${BUT_BERECHTIGUNG_DURCH}
          ${this.form.field('buts', 0, 'BuTBerechtigungDurch')}
          placeholder="BuT-berechtigt durch"
        ></cr-dropdown-selection>
      </cr-flex>
    `;
  }
}
